import { IresDeviceResponseReader } from "@/utils/IresDeviceResponseReader";
import { hasCapabilityForVersion, DeviceCapability } from "./DeviceCapability";

export interface ComBoxDryingConfigInfo {
  id: string;
  comBoxFirmwareVersion: string;
  dataBase64: string;
}

export interface ComBoxDryingProgram {
  name: string;
  programNumber: number;
  locked: boolean;
  needsReferenceSensor: boolean;
}

/**
 * The programs a HyDry Mini's firmware runs: Monitor (3) and Raumtrocknung (4). Target drying (5)
 * is not supported yet, although the Mini's program table has a slot for it ("Drying5"). Both the
 * client config dialog and the start / change dialog offer exactly these, so they cannot drift.
 */
export const MINI_SUPPORTED_DRYING_PROGRAMS = [3, 4];

export function getComBoxDryingProgramsDryingConfigInfo(
  comBoxDryingConfigInfo: ComBoxDryingConfigInfo,
  serialNumber: string
) {
  const binaryString = window.atob(comBoxDryingConfigInfo.dataBase64);

  const length = binaryString.length;
  const bytes = new Uint8Array(length);

  for (let i = 0; i < length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const iresDeviceResponseReader = new IresDeviceResponseReader(bytes);

  if (
    hasCapabilityForVersion(
      serialNumber,
      comBoxDryingConfigInfo.comBoxFirmwareVersion,
      DeviceCapability.DRYING_CONFIG_NAME
    )
  ) {
    //Skip config name which was added with firmware 0.13.7
    iresDeviceResponseReader.readString(8);
  }

  const dryingPrograms: ComBoxDryingProgram[] = [];

  const hasNeedsReferenceSensorInfo = hasCapabilityForVersion(
    serialNumber,
    comBoxDryingConfigInfo.comBoxFirmwareVersion,
    DeviceCapability.SENSOR_USAGE_INFO
  );

  for (let i = 0; i < 8; i++) {
    const programName = iresDeviceResponseReader.readString(16);
    // If bit 0 is set, it means the program needs a reference sensor
    const needsReferenceSensor = hasNeedsReferenceSensorInfo
      ? (iresDeviceResponseReader.readUint8() & 0x01) !== 0
      : i == 2;

    const locked = hasNeedsReferenceSensorInfo
      ? iresDeviceResponseReader.readBoolean()
      : false;

    if (hasNeedsReferenceSensorInfo) {
      iresDeviceResponseReader.skip(5);
    }

    dryingPrograms.push({
      programNumber: i,
      locked: locked,
      name: programName,
      needsReferenceSensor: needsReferenceSensor,
    });

    //For now skip step info (4 steps with 8 bytes each)
    iresDeviceResponseReader.skip(4 * 8);
  }

  return dryingPrograms;
}

function getDefaultProgramName(vue: Vue, programNumber: number) {
  if (programNumber === 0) {
    return vue.$t("comBoxDetail.monitor").toString();
  }

  if (programNumber === 1) {
    return vue.$t("comBoxDetail.programDryingTime").toString();
  }

  if (programNumber === 2) {
    return vue.$t("comBoxDetail.programDryingSensor").toString();
  }

  return vue.$t("comBoxDetail.formatProgram", [programNumber]).toString();
}

export function getDefaultDryingPrograms(vue: Vue) {
  const programs: ComBoxDryingProgram[] = [];

  for (let programNumber = 0; programNumber < 8; programNumber++) {
    programs.push({
      locked: false,
      programNumber: programNumber,
      name: getDefaultProgramName(vue, programNumber),
      needsReferenceSensor: programNumber == 2,
    });
  }

  return programs;
}
